import { useAction, useAtom } from '@reatom/npm-react'
import { useState } from 'react'
import styled from 'styled-components'

import { Typography, useHapticFeedback } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { useWalletAssetBalance } from '@root/entities/wallet/hooks'
import {
  setTradingWalletAction,
  tradeSwapTypeAtom,
  tradingWalletAtom,
} from '@root/features/trade/trading'
import { AmountFormatted } from '@root/shared/core/format'
import { ChevronDownExactIcon, WalletIcon } from '@root/shared/ui-kit'
import { WalletSelectorDrawer } from '@root/widgets/wallet'

import type { Pair } from '@root/entities/pair'
import type { WalletModel } from '@root/entities/wallet'

type Props = {
  pair: Pair.PairMinimal
}

export const TradingControlsWallet: React.FC<Props> = ({ pair }) => {
  const [wallet] = useAtom(tradingWalletAtom)

  if (!wallet) {
    return undefined
  }
  return <TradingControlsWallet_ pair={pair} wallet={wallet} />
}

const TradingControlsWallet_: React.FC<Props & { wallet: WalletModel }> = ({
  pair,
  wallet,
}) => {
  const [type] = useAtom(tradeSwapTypeAtom)
  const [walletsOpen, setWalletsOpen] = useState(false)
  const setActiveWallet = useAction(setTradingWalletAction)
  const haptics = useHapticFeedback()
  const [color] = useAtom(wallet.color)

  const currency = CurrencyModel.toNativeIfWrapped(
    type === 'buy' ? pair.base : pair.quote,
  )

  const balance = useWalletAssetBalance({
    currency,
    wallet,
  })

  return (
    <>
      <Root
        onClick={() => {
          haptics.impactOccurred('light')
          setWalletsOpen(true)
        }}
      >
        <Start>
          <Top>
            <WalletIcon color={color} size={14} />
            <WalletAddress>...{wallet.address.slice(-4)}</WalletAddress>
          </Top>
          <Balance>
            <AmountFormatted amount={balance} roundingMode="trunc" />
            <CurrencyIcon
              withGoogleLens={false}
              currency={currency}
              size={14}
            />
          </Balance>
        </Start>
        <End>
          <ChevronDownExactIcon size={9} />
        </End>
      </Root>

      <WalletSelectorDrawer
        open={walletsOpen}
        onOpenChange={setWalletsOpen}
        selected={wallet.id}
        onWalletClick={(wallet) => {
          setActiveWallet(wallet.id)
          setWalletsOpen(false)
        }}
      />
    </>
  )
}

const Root = styled.div`
  cursor: pointer;
  display: grid;
  justify-content: end;
  align-items: stretch;
  grid-template-columns: auto auto;
  grid-column: wallet;

  background: var(--background-2);
  border-radius: 8px;
  min-width: 86px;
`

const Start = styled.div`
  display: grid;
  align-items: center;
  padding-block: 5px;
  padding-inline: 7px;
`

const Top = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  gap: 3px;
  justify-content: start;
  align-items: center;
`

const Balance = styled(Typography).attrs({
  variant: 'body5-bold',
  component: 'div',
})`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 4px;

  color: var(--text-11);

  grid-template-columns: auto 1fr;
`

const WalletAddress = styled(Typography).attrs({
  variant: 'description-medium',
  component: 'div',
})`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 3px;

  color: var(--text-9);
`

const End = styled.div`
  display: grid;
  border-left: 1px solid var(--backgroundA-3);
  align-items: center;
  justify-content: center;
  color: var(--text-9);
  width: 17px;
`
